// Spec: specs/stats/questions/functional_requirements.md §6.10
//
// What each state says. Kept apart from the resolution so a type can be given words without
// touching the logic that decided which words it needs.

import { CAUSE, thresholdFor } from '@aytm/app/stats/page/question-views/components/data-run/lib/causes'

const NAMES = {
  md: 'Advanced MaxDiff HB',
  cjd: 'Conjoint Segmentation',
  cjc: 'Conjoint Express',
  bpm: 'the perceptual map',
  hm: 'the heatmap',
  vk: 'the pricing model'
}

const nameFor = type => NAMES[type] || 'this analysis'

// A run costs the reader time, so the button says what it costs. Everything else about the wait is
// the report's business, not the button's.
export const RUN_LABEL = 'Run the report (5-15 min)'
export const RUNNING_LABEL = 'Running the report'

export const calcCopy = ({ cause, type, missing = 'image' } = {}) => {
  switch (cause) {
    case CAUSE.belowThreshold: {
      const needed = thresholdFor(type)

      return {
        title: 'Not enough responses yet',
        detail: needed
          ? `${nameFor(type)} needs at least ${needed} completes to calculate`
          : `${nameFor(type)} needs more completes to calculate`
      }
    }
    case CAUSE.notCalculated:
      // Says what is so without saying whose fault it is: nothing here knows whether a run was
      // never asked for or is simply not on offer to this reader.
      return {
        title: 'Not calculated yet',
        detail: `${nameFor(type)} has not been calculated for this report`
      }
    case CAUSE.notYetCalculated:
      return {
        title: 'Not all of your sample is in yet',
        detail: 'You can calculate on what has been collected so far, but the figures can still move'
      }
    case CAUSE.calculating:
      return { title: 'Crunching the numbers', detail: `Working on ${nameFor(type)}` }
    case CAUSE.couldNotCalculate:
      // Not attributed to the sample -- the analysis was attempted and did not resolve -- but not
      // left as a dead end either: more responses genuinely can change it, and saying so is the
      // difference between a state that reports and a state that only laments.
      return {
        title: 'We could not calculate this one',
        detail: `${nameFor(type)} could not be resolved from these responses yet - more may change that`
      }
    case CAUSE.stillFilling:
      return { title: 'Waiting for responses', detail: 'This builds itself as completes arrive' }
    case CAUSE.notSetUp:
      // the noun is bare: an article here reads as "no an image", and the second line does not
      // need one either once it refers back rather than repeating the noun
      return {
        title: `This question has no ${missing}`,
        detail: `Add one in the survey editor and this will draw`
      }
    default:
      return null
  }
}
